export interface StudentCertificateStatistic {
  label: string;
  value: string;
}

export interface StudentCertificateDesignData {
  certificateTitle: string;
  certificateNumber: string;
  studentName: string;
  examName?: string;
  courseName?: string;
  scholarshipName?: string;
  className?: string;
  subjectName?: string;
  score?: number;
  maximumScore?: number;
  percentage?: number;
  grade?: string;
  rank?: number;
  participantCount?: number;
  achievementBadge?: string;
  achievementMessage: string;
  issueDate: string;
  issuedByName: string;
  authorizedSignatoryName?: string;
  authorizedSignatoryDesignation?: string;
  logoUrl?: string;
  sealUrl?: string;
  signatureUrl?: string;
  qrCodeDataUrl?: string;
  verificationUrl: string;
  stats?: StudentCertificateStatistic[];
}

export interface ExamCertificateSummary {
  certificateId: string;
  attemptId: string;
  examId: string;
  title: string;
  subject: string;
  className?: string;
  standardLevel?: string;
  score?: number;
  maximumScore?: number;
  percentageScore?: number;
  percentage?: number;
  grade?: string;
  rank?: number;
  participantCount?: number;
  issuedAt?: string;
  verificationUrl?: string;
}

export interface ScholarshipCertificateLike {
  certificateId: string;
  registrationId: string;
  scholarshipId: string;
  scholarshipTitle: string;
  studentName: string;
  classLevel?: string;
  schoolName?: string;
  status?: string;
  score?: number;
  percentageScore?: number;
  rank?: number;
  finalRank?: number;
  prizeTitle?: string;
  prizeAmount?: number;
  issuedAt: string;
  sponsorName?: string;
  memorialName?: string;
  certificateType?: 'PARTICIPATION' | 'MERIT';
  certificateText?: string;
  poweredByTagline?: string;
}

const DEFAULT_ISSUER = 'LocalAlly Education';
const DEFAULT_SIGNATORY = 'R. K. Verma';
const DEFAULT_SIGNATORY_DESIGNATION = 'Authorized Signatory';

export const CERTIFICATE_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Great+Vibes&family=Inter:wght@400;500;600;700;800&display=swap');

.la-certificate,
.la-certificate * {
  box-sizing: border-box;
}

.la-certificate {
  --navy: #082747;
  --navy-dark: #03182d;
  --gold: #bf8522;
  --gold-soft: #e9c46f;
  --gold-pale: #f8e4ac;
  --ivory: #fffdf7;
  --ink: #0c1935;
  --muted: #576173;
  --purple: #5b44c4;
  position: relative;
  width: 1123px;
  height: 794px;
  overflow: hidden;
  color: var(--ink);
  background:
    radial-gradient(circle at 16% 16%, rgba(232, 187, 88, 0.28), transparent 19%),
    radial-gradient(circle at 95% 86%, rgba(8, 39, 71, 0.17), transparent 24%),
    linear-gradient(180deg, #fffefa 0%, var(--ivory) 100%);
  font-family: 'Inter', Arial, sans-serif;
  box-shadow: 0 28px 90px rgba(15, 23, 42, 0.18);
}

.la-certificate::before {
  position: absolute;
  top: -128px;
  left: -126px;
  width: 520px;
  height: 250px;
  content: '';
  border-radius: 0 0 100% 0;
  background: linear-gradient(132deg, rgba(191, 133, 34, 0.94), rgba(249, 222, 142, 0.22) 76%);
  transform: rotate(-7deg);
}

.la-certificate::after {
  position: absolute;
  right: -156px;
  bottom: -160px;
  width: 390px;
  height: 470px;
  content: '';
  border-radius: 100% 0 0 0;
  background:
    linear-gradient(132deg, transparent 0 32%, var(--gold) 33% 39%, transparent 40% 51%, var(--navy) 52% 100%);
}

.la-frame-outer {
  position: absolute;
  inset: 14px;
  z-index: 1;
  border: 8px solid var(--navy);
  background: rgba(255, 253, 247, 0.62);
}

.la-frame-gold {
  position: absolute;
  inset: 8px;
  border: 3px solid var(--gold);
}

.la-frame-inner {
  position: absolute;
  inset: 8px;
  overflow: hidden;
  border: 1px solid var(--gold-soft);
}

.la-frame-inner::before {
  position: absolute;
  inset: 20px;
  content: '';
  border: 1px solid rgba(191, 133, 34, 0.28);
  pointer-events: none;
}

.la-wave-lines {
  position: absolute;
  inset: 0;
  opacity: 0.36;
  background:
    repeating-radial-gradient(ellipse at 74% 38%, transparent 0 18px, rgba(8, 39, 71, 0.08) 19px 20px),
    repeating-radial-gradient(ellipse at 15% 86%, transparent 0 16px, rgba(8, 39, 71, 0.08) 17px 18px);
}

.la-content {
  position: relative;
  z-index: 3;
  height: 100%;
  padding: 42px 70px 42px;
  text-align: center;
}

.la-corner {
  position: absolute;
  z-index: 4;
  width: 62px;
  height: 62px;
  border-color: var(--gold);
  pointer-events: none;
}

.la-corner-tl {
  top: 24px;
  left: 24px;
  border-top: 3px solid;
  border-left: 3px solid;
}

.la-corner-tr {
  top: 24px;
  right: 24px;
  border-top: 3px solid;
  border-right: 3px solid;
}

.la-corner-bl {
  bottom: 24px;
  left: 24px;
  border-bottom: 3px solid;
  border-left: 3px solid;
}

.la-corner-br {
  right: 24px;
  bottom: 24px;
  border-right: 3px solid;
  border-bottom: 3px solid;
}

.la-ribbon {
  position: absolute;
  top: 0;
  left: 76px;
  z-index: 6;
  width: 116px;
  height: 198px;
  padding-top: 42px;
  color: #fff;
  background: linear-gradient(180deg, #132f4f 0%, #061c35 100%);
  border-right: 3px solid var(--gold);
  border-left: 3px solid var(--gold);
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.07);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 83%, 0 100%);
}

.la-ribbon::before {
  display: block;
  width: 66px;
  height: 46px;
  margin: 0 auto 14px;
  content: '';
  background:
    linear-gradient(27deg, transparent 44%, var(--gold-pale) 45% 55%, transparent 56%),
    linear-gradient(153deg, transparent 44%, var(--gold-pale) 45% 55%, transparent 56%);
  clip-path: polygon(50% 0, 100% 32%, 50% 64%, 0 32%);
}

.la-ribbon-label {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  color: var(--gold-pale);
}

.la-ribbon-stars {
  margin-top: 11px;
  font-size: 18px;
  letter-spacing: 6px;
  color: var(--gold-soft);
}

.la-top-row {
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr) 230px;
  align-items: start;
  min-height: 104px;
}

.la-brand {
  display: inline-flex;
  grid-column: 2;
  align-items: center;
  justify-content: center;
  gap: 16px;
  justify-self: center;
}

.la-brand-mark {
  display: grid;
  width: 62px;
  height: 62px;
  place-items: center;
  color: var(--purple);
  border: 5px solid currentColor;
  border-radius: 50% 50% 50% 18%;
  font-size: 23px;
  font-weight: 800;
  line-height: 1;
  transform: rotate(-45deg);
}

.la-brand-mark span {
  transform: rotate(45deg);
}

.la-brand-title {
  margin: 0;
  text-align: left;
  font-size: 42px;
  font-weight: 800;
  line-height: 0.95;
  color: var(--navy-dark);
  letter-spacing: 0;
}

.la-brand-tagline {
  margin-top: 8px;
  text-align: left;
  font-size: 18px;
  font-weight: 500;
  color: #1e293b;
}

.la-certificate-number {
  grid-column: 3;
  justify-self: end;
  max-width: 230px;
  padding-top: 6px;
  text-align: right;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ink);
}

.la-certificate-number strong {
  display: block;
  margin-top: 3px;
  overflow-wrap: anywhere;
  font-size: 16px;
}

.la-title {
  margin-top: 12px;
  font-family: 'Cinzel', Georgia, serif;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.06;
  color: #9c6615;
  letter-spacing: 11px;
  text-transform: uppercase;
}

.la-title-divider {
  position: relative;
  width: 560px;
  height: 2px;
  margin: 18px auto 0;
  background: linear-gradient(to right, transparent, var(--gold), transparent);
}

.la-title-divider::after {
  position: absolute;
  top: -6px;
  left: 50%;
  width: 14px;
  height: 14px;
  content: '';
  background: var(--gold);
  transform: translateX(-50%) rotate(45deg);
  box-shadow: -18px 18px 0 -6px var(--gold), 18px -18px 0 -6px var(--gold);
}

.la-certify {
  margin-top: 20px;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 2px;
}

.la-student-name {
  display: inline-block;
  max-width: 660px;
  min-width: 520px;
  margin-top: 13px;
  padding: 0 32px 12px;
  overflow-wrap: anywhere;
  border-bottom: 2px solid rgba(191, 133, 34, 0.72);
  font-family: 'Great Vibes', cursive;
  font-size: 68px;
  line-height: 0.94;
  color: var(--navy);
}

.la-name-long {
  font-size: 54px;
  line-height: 1.02;
}

.la-name-extra-long {
  font-size: 44px;
  line-height: 1.08;
}

.la-achievement-text {
  max-width: 660px;
  margin: 18px auto 0;
  font-size: 20px;
  line-height: 1.42;
}

.la-achievement-text strong,
.la-highlight {
  color: var(--navy);
  font-weight: 800;
}

.la-class-subject {
  margin-top: 8px;
  font-size: 21px;
  font-weight: 800;
  color: var(--navy);
}

.la-class-subject span {
  display: inline-block;
  margin: 0 18px;
  color: var(--gold);
  font-weight: 500;
}

.la-message {
  max-width: 690px;
  min-height: 39px;
  margin: 14px auto 0;
  color: var(--ink);
  font-size: 14px;
  line-height: 1.45;
}

.la-achievement-badge {
  position: absolute;
  top: 334px;
  left: 80px;
  z-index: 4;
  width: 168px;
  height: 168px;
  display: grid;
  place-items: center;
  color: #fff;
  background: radial-gradient(circle, #062342 0 54%, #021426 55% 63%, var(--gold) 64% 100%);
  border-radius: 50%;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.16);
}

.la-achievement-badge::before,
.la-achievement-badge::after {
  position: absolute;
  bottom: -70px;
  width: 42px;
  height: 86px;
  content: '';
  background: linear-gradient(180deg, var(--navy), #03172c);
  border-right: 2px solid var(--gold);
  border-left: 2px solid var(--gold);
}

.la-achievement-badge::before {
  left: 44px;
  transform: rotate(15deg);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%);
}

.la-achievement-badge::after {
  right: 44px;
  transform: rotate(-15deg);
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%);
}

.la-badge-inner {
  width: 124px;
  height: 124px;
  display: flex;
  position: relative;
  z-index: 2;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 2px solid var(--gold-pale);
  border-radius: 50%;
}

.la-badge-stars {
  margin-bottom: 8px;
  color: var(--gold-soft);
  font-size: 20px;
  letter-spacing: 4px;
}

.la-badge-text {
  font-size: 19px;
  font-weight: 800;
  line-height: 1.15;
  text-transform: uppercase;
}

.la-growth-wreath {
  position: absolute;
  top: 260px;
  right: 76px;
  width: 176px;
  height: 176px;
  display: grid;
  place-items: center;
  color: var(--navy);
}

.la-growth-wreath::before {
  position: absolute;
  inset: 0;
  content: '';
  border: 10px solid var(--gold);
  border-top-color: transparent;
  border-bottom-color: transparent;
  border-radius: 50%;
  transform: rotate(22deg);
  opacity: 0.9;
}

.la-growth-copy {
  width: 112px;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.35;
  text-transform: uppercase;
}

.la-stats-row {
  display: flex;
  justify-content: center;
  gap: 10px;
  max-width: 650px;
  margin: 26px auto 0;
}

.la-stat-card {
  min-width: 142px;
  max-width: 164px;
  min-height: 68px;
  padding: 10px 13px;
  border: 1px solid var(--gold-soft);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.86);
}

.la-stat-label {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.la-stat-value {
  margin-top: 6px;
  color: var(--navy);
  font-size: 20px;
  font-weight: 800;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.la-bottom-row {
  position: absolute;
  right: 78px;
  bottom: 74px;
  left: 78px;
  display: grid;
  grid-template-columns: 150px 210px minmax(120px, 1fr) 210px 126px;
  align-items: end;
  gap: 24px;
}

.la-meta {
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}

.la-meta-label {
  display: block;
  margin-bottom: 7px;
  color: var(--ink);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.la-seal {
  justify-self: center;
  width: 132px;
  height: 132px;
  display: grid;
  place-items: center;
  color: var(--gold-pale);
  background: radial-gradient(circle, #0a294a 0 52%, #04192f 53% 64%, var(--gold) 65% 100%);
  border-radius: 50%;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.12);
}

.la-seal img {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.la-seal-fallback {
  display: flex;
  width: 98px;
  height: 98px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--gold-pale);
  border-radius: 50%;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
}

.la-seal-fallback span {
  margin-top: 7px;
  font-size: 13px;
  letter-spacing: 4px;
}

.la-signature img {
  width: 130px;
  height: 44px;
  object-fit: contain;
}

.la-signature-name {
  font-family: 'Great Vibes', cursive;
  font-size: 34px;
  line-height: 1;
  color: #111827;
}

.la-signature-line {
  width: 170px;
  margin: 7px auto 6px;
  border-top: 1px solid var(--gold);
}

.la-signature-title {
  color: var(--purple);
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.la-signature-designation {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
}

.la-verification {
  justify-self: end;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
}

.la-verification img {
  width: 96px;
  height: 96px;
  padding: 7px;
  object-fit: contain;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
}

.la-verification-text {
  margin-top: 6px;
}

.la-verification-footer {
  position: absolute;
  right: 172px;
  bottom: 0;
  left: 172px;
  z-index: 5;
  min-height: 39px;
  padding: 12px 28px 10px;
  overflow: hidden;
  color: #fff;
  background: linear-gradient(90deg, #082747 0%, #061c35 100%);
  clip-path: polygon(3% 0, 97% 0, 100% 100%, 0 100%);
  font-size: 14px;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media print {
  @page {
    size: A4 landscape;
    margin: 0;
  }

  html,
  body {
    width: 297mm;
    height: 210mm;
    margin: 0;
    background: #fff;
  }

  .la-certificate {
    width: 297mm;
    height: 210mm;
    box-shadow: none;
  }
}
`;

const escapeHtml = (value?: string | number) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const compactWhitespace = (value?: string) => String(value || '').replace(/\s+/g, ' ').trim();

export const getCertificateOrigin = () => {
  const configured = String(process.env.REACT_APP_PUBLIC_SITE_URL || '').trim();
  if (configured) return configured.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://localally.in';
};

export const formatCertificateDate = (value?: string | Date) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export const formatCertificatePercentage = (value?: number) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return `${numeric.toFixed(2)}%`;
};

export const formatCertificateNumber = (value?: number) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return new Intl.NumberFormat('en-IN').format(numeric);
};

export const getCertificateVerificationUrl = (certificateNumber: string, origin = getCertificateOrigin()) =>
  `${origin}/certificate/verify/${encodeURIComponent(certificateNumber)}`;

export const getQrCodeUrl = (verificationUrl: string) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=10&data=${encodeURIComponent(verificationUrl)}`;

export const getStudentNameClass = (studentName: string) => {
  const length = compactWhitespace(studentName).length;
  if (length > 44) return 'la-name-extra-long';
  if (length > 30) return 'la-name-long';
  return '';
};

export const resolveCertificateStats = (certificate: StudentCertificateDesignData): StudentCertificateStatistic[] => {
  if (Array.isArray(certificate.stats)) {
    return certificate.stats.filter((stat) => compactWhitespace(stat.label) && compactWhitespace(stat.value));
  }

  const stats: StudentCertificateStatistic[] = [];
  const hasScore = certificate.score !== undefined && certificate.score !== null;
  const hasMaximumScore = certificate.maximumScore !== undefined && certificate.maximumScore !== null;
  const hasPercentage = certificate.percentage !== undefined && certificate.percentage !== null;
  const hasGrade = Boolean(compactWhitespace(certificate.grade));
  const hasRank = certificate.rank !== undefined && certificate.rank !== null;

  if (hasScore) {
    stats.push({
      label: 'Score',
      value: hasMaximumScore
        ? `${formatCertificateNumber(certificate.score)} / ${formatCertificateNumber(certificate.maximumScore)}`
        : formatCertificateNumber(certificate.score),
    });
  }

  if (hasPercentage) {
    stats.push({ label: 'Percentage', value: formatCertificatePercentage(certificate.percentage) });
  }

  if (hasGrade) {
    stats.push({ label: 'Grade', value: String(certificate.grade) });
  }

  if (hasRank) {
    const rankValue = formatCertificateNumber(certificate.rank);
    const countValue = certificate.participantCount ? formatCertificateNumber(certificate.participantCount) : '';
    stats.push({ label: 'Rank', value: countValue ? `${rankValue} / ${countValue}` : rankValue });
  }

  return stats;
};

const getGradeFromPercentage = (percentage?: number) => {
  const value = Number(percentage || 0);
  if (value >= 90) return 'A+';
  if (value >= 80) return 'A';
  if (value >= 70) return 'B+';
  if (value >= 60) return 'B';
  if (value >= 50) return 'C';
  return 'Pass';
};

const getDefaultStudentName = (studentName?: string) => compactWhitespace(studentName) || 'Student';

export const buildExamCertificateDesignData = (
  certificate: ExamCertificateSummary,
  studentName?: string,
  origin = getCertificateOrigin(),
): StudentCertificateDesignData => {
  const percentage = Number(certificate.percentageScore ?? certificate.percentage ?? 0);
  const verificationUrl = certificate.verificationUrl ||
    getCertificateVerificationUrl(certificate.certificateId, origin);

  return {
    certificateTitle: 'Merit Certificate',
    certificateNumber: certificate.certificateId,
    studentName: getDefaultStudentName(studentName),
    examName: certificate.title || 'LocalAlly Exam',
    className: certificate.className || certificate.standardLevel || '',
    subjectName: certificate.subject || 'General',
    score: certificate.score,
    maximumScore: certificate.maximumScore,
    percentage,
    grade: certificate.grade || getGradeFromPercentage(percentage),
    rank: certificate.rank,
    participantCount: certificate.participantCount,
    achievementBadge: percentage >= 90 ? 'Great Achievement' : 'Merit Award',
    achievementMessage: 'We appreciate your hard work, dedication and outstanding performance. Keep learning and keep growing!',
    issueDate: formatCertificateDate(certificate.issuedAt),
    issuedByName: DEFAULT_ISSUER,
    authorizedSignatoryName: DEFAULT_SIGNATORY,
    authorizedSignatoryDesignation: DEFAULT_SIGNATORY_DESIGNATION,
    qrCodeDataUrl: getQrCodeUrl(verificationUrl),
    verificationUrl,
  };
};

const getScholarshipMessage = (certificate: ScholarshipCertificateLike, isMerit: boolean) => {
  const raw = compactWhitespace(certificate.certificateText?.replace(/\[Student Name\]/g, certificate.studentName));
  if (raw) return raw;
  if (isMerit) {
    return 'We appreciate your hard work, dedication and outstanding performance. Keep learning and keep growing!';
  }
  return `This certificate is awarded for successfully participating in ${certificate.scholarshipTitle} conducted through LocalAlly Education.`;
};

export const buildScholarshipCertificateDesignData = (
  certificate: ScholarshipCertificateLike,
  origin = getCertificateOrigin(),
): StudentCertificateDesignData => {
  const isMerit = certificate.certificateType === 'MERIT' ||
    certificate.status === 'SELECTED' ||
    Number(certificate.finalRank || 0) > 0;
  const percentage = Number(certificate.percentageScore || 0);
  const rank = Number(certificate.finalRank || certificate.rank || 0) || undefined;
  const verificationUrl = getCertificateVerificationUrl(certificate.certificateId, origin);
  const stats = isMerit
    ? resolveCertificateStats({
        certificateTitle: '',
        certificateNumber: certificate.certificateId,
        studentName: certificate.studentName,
        score: certificate.score,
        percentage,
        rank,
        achievementMessage: '',
        issueDate: '',
        issuedByName: '',
        verificationUrl,
      })
    : [];

  if (isMerit && certificate.prizeAmount) {
    stats.push({
      label: 'Prize',
      value: new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(certificate.prizeAmount),
    });
  }

  return {
    certificateTitle: isMerit ? 'Scholarship Achievement Certificate' : 'Certificate of Participation',
    certificateNumber: certificate.certificateId,
    studentName: getDefaultStudentName(certificate.studentName),
    examName: certificate.scholarshipTitle || 'LocalAlly Scholarship Exam',
    scholarshipName: certificate.scholarshipTitle,
    className: certificate.classLevel || '',
    subjectName: isMerit ? certificate.prizeTitle || 'Scholarship Exam' : 'Scholarship Exam',
    score: isMerit ? certificate.score : undefined,
    percentage: isMerit ? percentage : undefined,
    rank: isMerit ? rank : undefined,
    achievementBadge: isMerit ? certificate.prizeTitle || 'Scholarship Winner' : 'Proud Participant',
    achievementMessage: getScholarshipMessage(certificate, isMerit),
    issueDate: formatCertificateDate(certificate.issuedAt),
    issuedByName: certificate.sponsorName || DEFAULT_ISSUER,
    authorizedSignatoryName: DEFAULT_SIGNATORY,
    authorizedSignatoryDesignation: DEFAULT_SIGNATORY_DESIGNATION,
    qrCodeDataUrl: getQrCodeUrl(verificationUrl),
    verificationUrl,
    stats,
  };
};

const renderStatsHtml = (certificate: StudentCertificateDesignData) => {
  const stats = resolveCertificateStats(certificate);
  if (!stats.length) return '';
  return `
    <div class="la-stats-row">
      ${stats.map((stat) => `
        <div class="la-stat-card">
          <div class="la-stat-label">${escapeHtml(stat.label)}</div>
          <div class="la-stat-value">${escapeHtml(stat.value)}</div>
        </div>
      `).join('')}
    </div>
  `;
};

const renderClassSubjectHtml = (certificate: StudentCertificateDesignData) => {
  const className = compactWhitespace(certificate.className);
  const subjectName = compactWhitespace(certificate.subjectName);
  if (!className && !subjectName) return '';
  if (className && subjectName) {
    return `<div class="la-class-subject">${escapeHtml(className)}<span>|</span>${escapeHtml(subjectName)}</div>`;
  }
  return `<div class="la-class-subject">${escapeHtml(className || subjectName)}</div>`;
};

const renderAchievementHtml = (certificate: StudentCertificateDesignData) => {
  const examName = compactWhitespace(certificate.examName || certificate.courseName || certificate.scholarshipName);
  const percentage = certificate.percentage !== undefined ? formatCertificatePercentage(certificate.percentage) : '';
  if (percentage && examName) {
    return `
      <div class="la-achievement-text">
        has achieved <span class="la-highlight">${escapeHtml(percentage)}</span> in the
        <strong>${escapeHtml(examName)}</strong>
      </div>
    `;
  }

  if (examName) {
    return `
      <div class="la-achievement-text">
        has successfully accomplished <strong>${escapeHtml(examName)}</strong>
      </div>
    `;
  }

  return '';
};

const renderCertificateBodyHtml = (certificate: StudentCertificateDesignData) => {
  const nameClass = getStudentNameClass(certificate.studentName);
  const qrCodeUrl = certificate.qrCodeDataUrl || getQrCodeUrl(certificate.verificationUrl);

  return `
    <main class="la-certificate" aria-label="${escapeHtml(certificate.certificateTitle)} for ${escapeHtml(certificate.studentName)}">
      <div class="la-frame-outer">
        <div class="la-frame-gold">
          <div class="la-frame-inner">
            <div class="la-wave-lines"></div>
            <div class="la-corner la-corner-tl"></div>
            <div class="la-corner la-corner-tr"></div>
            <div class="la-corner la-corner-bl"></div>
            <div class="la-corner la-corner-br"></div>
            <div class="la-ribbon" aria-hidden="true">
              <div class="la-ribbon-label">LA</div>
              <div class="la-ribbon-stars">* * *</div>
            </div>
            <section class="la-content">
              <div class="la-top-row">
                <div></div>
                <div class="la-brand">
                  <div class="la-brand-mark"><span>LA</span></div>
                  <div>
                    <h1 class="la-brand-title">LocalAlly</h1>
                    <div class="la-brand-tagline">Learn. Compete. Grow.</div>
                  </div>
                </div>
                <div class="la-certificate-number">
                  Certificate No.
                  <strong>${escapeHtml(certificate.certificateNumber)}</strong>
                </div>
              </div>
              <div class="la-title">${escapeHtml(certificate.certificateTitle)}</div>
              <div class="la-title-divider"></div>
              <div class="la-certify">THIS IS TO CERTIFY THAT</div>
              <div class="la-student-name ${nameClass}">${escapeHtml(certificate.studentName)}</div>
              ${renderAchievementHtml(certificate)}
              ${renderClassSubjectHtml(certificate)}
              <div class="la-message">${escapeHtml(certificate.achievementMessage)}</div>
              ${renderStatsHtml(certificate)}
            </section>
            ${certificate.achievementBadge ? `
              <div class="la-achievement-badge">
                <div class="la-badge-inner">
                  <div class="la-badge-stars">* * *</div>
                  <div class="la-badge-text">${escapeHtml(certificate.achievementBadge)}</div>
                </div>
              </div>
            ` : ''}
            <div class="la-growth-wreath" aria-hidden="true">
              <div class="la-growth-copy">Keep Learning Keep Growing</div>
            </div>
            <div class="la-bottom-row">
              <div class="la-meta">
                <span class="la-meta-label">Issue Date</span>
                ${escapeHtml(certificate.issueDate)}
              </div>
              <div class="la-meta">
                <span class="la-meta-label">Issued By</span>
                ${escapeHtml(certificate.issuedByName)}
              </div>
              <div class="la-seal">
                ${certificate.sealUrl
                  ? `<img src="${escapeHtml(certificate.sealUrl)}" alt="LocalAlly seal" />`
                  : '<div class="la-seal-fallback">LocalAlly<span>* * *</span></div>'}
              </div>
              <div class="la-meta la-signature">
                ${certificate.signatureUrl
                  ? `<img src="${escapeHtml(certificate.signatureUrl)}" alt="Authorized signature" />`
                  : `<div class="la-signature-name">${escapeHtml(certificate.authorizedSignatoryName || DEFAULT_SIGNATORY)}</div>`}
                <div class="la-signature-line"></div>
                <div class="la-signature-title">Authorized Signatory</div>
                <div class="la-signature-designation">${escapeHtml(certificate.authorizedSignatoryDesignation || DEFAULT_SIGNATORY_DESIGNATION)}</div>
              </div>
              <div class="la-verification">
                <img src="${escapeHtml(qrCodeUrl)}" alt="Certificate verification QR code" />
                <div class="la-verification-text">Scan to Verify</div>
              </div>
            </div>
            <div class="la-verification-footer">
              Verify this certificate at: ${escapeHtml(certificate.verificationUrl)}
            </div>
          </div>
        </div>
      </div>
    </main>
  `;
};

export const renderStudentCertificateHtml = (
  certificate: StudentCertificateDesignData,
  options: { autoPrint?: boolean } = {},
) => `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(certificate.certificateNumber)}</title>
  <style>
    ${CERTIFICATE_STYLES}
  </style>
  <style>
    @media screen {
      html,
      body {
        margin: 0;
        min-height: 100%;
        background: #ececec;
      }

      body {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
      }
    }
  </style>
</head>
<body>
  ${renderCertificateBodyHtml(certificate)}
  ${options.autoPrint ? `
    <script>
      window.addEventListener('load', function () {
        var printCertificate = function () {
          window.focus();
          window.print();
        };
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(function () {
            setTimeout(printCertificate, 500);
          }).catch(function () {
            setTimeout(printCertificate, 700);
          });
        } else {
          setTimeout(printCertificate, 700);
        }
      });
    </script>
  ` : ''}
</body>
</html>`;

export const downloadCertificateHtml = (certificate: StudentCertificateDesignData) => {
  const blob = new Blob([renderStudentCertificateHtml(certificate)], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${certificate.certificateNumber.replace(/[^a-z0-9-]+/gi, '-')}.html`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const openCertificatePrintWindow = (certificate: StudentCertificateDesignData) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    downloadCertificateHtml(certificate);
    return;
  }

  printWindow.opener = null;
  printWindow.document.open();
  printWindow.document.write(renderStudentCertificateHtml(certificate, { autoPrint: true }));
  printWindow.document.close();
};
